import { useState, useEffect, useCallback } from 'react';
import {
  getCapabilities,
  getRoles,
  getUsers,
  getResourceTypes,
  getUserPermissions,
  getRoleCapabilities,
  getResources,
  getResourceTags,
} from '../services/authorizationService';

export const useCapabilities = () => {
  const [data, setData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetch = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);
      const result = await getCapabilities();
      setData(result);
    } catch (e) {
      setError(e);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => { fetch(); }, [fetch]);
  return { data, isLoading, error, refetch: fetch };
};

export const useRoles = () => {
  const [data, setData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetch = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);
      const result = await getRoles();
      setData(result);
    } catch (e) {
      setError(e);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => { fetch(); }, [fetch]);
  return { data, isLoading, error, refetch: fetch };
};

export const useUsers = () => {
  const [data, setData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetch = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);
      const result = await getUsers();
      setData(result);
    } catch (e) {
      setError(e);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => { fetch(); }, [fetch]);
  return { data, isLoading, error, refetch: fetch };
};

export const useResourceTypes = () => {
  const [data, setData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  const fetch = useCallback(async () => {
    try {
      setIsLoading(true);
      const result = await getResourceTypes();
      setData(result);
    } catch (e) {
      console.error(e);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => { fetch(); }, [fetch]);
  return { data, isLoading, refetch: fetch };
};

export const useUserPermissions = (userId) => {
  const [data, setData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetch = useCallback(async () => {
    if (!userId) return;
    try {
      setIsLoading(true);
      setError(null);
      const result = await getUserPermissions(userId);
      setData(result);
    } catch (e) {
      setError(e);
    } finally {
      setIsLoading(false);
    }
  }, [userId]);

  useEffect(() => { fetch(); }, [fetch]);
  return { data, isLoading, error, refetch: fetch };
};

export const useRoleCapabilities = (roleId) => {
  const [data, setData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  const fetch = useCallback(async () => {
    if (!roleId) return;
    try {
      setIsLoading(true);
      const result = await getRoleCapabilities(roleId);
      setData(result);
    } catch (e) {
      console.error(e);
    } finally {
      setIsLoading(false);
    }
  }, [roleId]);

  useEffect(() => { fetch(); }, [fetch]);
  return { data, isLoading, refetch: fetch };
};

export const useResources = (resourceType, parentId, filters) => {
  const [data, setData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  const fetch = useCallback(async () => {
    if (!resourceType) return;
    try {
      setIsLoading(true);
      const result = await getResources(resourceType, parentId, filters);
      setData(result);
    } catch (e) {
      console.error(e);
    } finally {
      setIsLoading(false);
    }
  }, [resourceType, parentId, JSON.stringify(filters)]);

  useEffect(() => { fetch(); }, [fetch]);
  return { data, isLoading, refetch: fetch };
};

export const useResourceTags = (resourceType) => {
  const [data, setData] = useState({});
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (!resourceType) return;
    setIsLoading(true);
    getResourceTags(resourceType)
      .then(setData)
      .catch(console.error)
      .finally(() => setIsLoading(false));
  }, [resourceType]);

  return { data, isLoading };
};